import type { MessageAnswer } from '../models/orpheus.types'

import { mapColumns, type ReportColumn, type ReportRow } from '@/features/reports'

export interface AnswerPayloadProjection {
  rows: ReportRow[]
  columns: ReportColumn[]
  dimensionColumns: ReportColumn[]
  metricColumns: ReportColumn[]
  timeDimensions: string[]
  summaryTotal: Record<string, unknown> | null
  hasComparison: boolean
}

export interface AnswerPayloadContext {
  getColumn: (field: string) => ReportColumn | null | undefined
}

function recordOrNull(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function nonEmptyRecord(value: unknown): Record<string, unknown> | null {
  const record = recordOrNull(value)
  return record && Object.keys(record).length > 0 ? record : null
}

function hasValues(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0
  const record = recordOrNull(value)
  return record !== null && Object.keys(record).length > 0
}

function isVisibleField(field: string): boolean {
  return !field.startsWith('prev_') && !field.endsWith('_url')
}

function isFiniteNumeric(value: unknown): boolean {
  if (typeof value === 'number') return Number.isFinite(value)
  if (typeof value !== 'string' || value.trim() === '') return false
  return Number.isFinite(Number(value))
}

function observedNumeric(rows: readonly ReportRow[], field: string): boolean {
  return rows.some((row) => isFiniteNumeric(row[field]))
}

function firstSeenFields(rows: readonly ReportRow[]): string[] {
  const fields: string[] = []
  const seen = new Set<string>()
  for (const row of rows) {
    for (const field of Object.keys(row)) {
      if (!seen.has(field) && isVisibleField(field)) {
        seen.add(field)
        fields.push(field)
      }
    }
  }
  return fields
}

/** Grounded chat raw_data -> module-08 presentation inputs; no Orpheus report engine. */
export function normalizeAnswerPayload(
  answer: MessageAnswer,
  context: AnswerPayloadContext,
): AnswerPayloadProjection {
  const source = answer.payload.combined ?? answer.payload
  const rows = [...source.data]
  const selectedDimensions = new Set(answer.selectedDimensions)
  const selectedMetrics = new Set(answer.selectedMetrics)
  const fields = [
    ...answer.selectedDimensions,
    ...answer.selectedMetrics,
    ...firstSeenFields(rows),
  ].filter(isVisibleField)
  const classifiedColumns = mapColumns(fields, context).map((column) => {
    if (selectedDimensions.has(column.field)) return { ...column, numeric: false }
    if (
      column.numeric ||
      selectedMetrics.has(column.field) ||
      observedNumeric(rows, column.field)
    ) {
      return { ...column, numeric: true }
    }
    return column
  })
  const classifiedByField = new Map(classifiedColumns.map((column) => [column.field, column]))
  const columns = mapColumns(fields, { getColumn: (field) => classifiedByField.get(field) })
  const dimensionColumns = columns.filter((column) => !column.numeric)
  const metricColumns = columns.filter((column) => column.numeric)
  const timeDimensions = dimensionColumns
    .filter(
      (column) => column.date_col || column.sub_type === 'date' || column.sub_type === 'datetime',
    )
    .map((column) => column.field)
  const pageTotal = nonEmptyRecord(source.page_total) ?? nonEmptyRecord(answer.payload.page_total)
  const total = nonEmptyRecord(source.total) ?? nonEmptyRecord(answer.payload.total)

  return {
    rows,
    columns,
    dimensionColumns,
    metricColumns,
    timeDimensions,
    summaryTotal: pageTotal ?? total,
    hasComparison:
      rows.some((row) => Object.keys(row).some((field) => field.startsWith('prev_'))) ||
      hasValues(source.previous_period),
  }
}
